import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

// Session-scoped so a browser refresh keeps the impersonated view,
// but closing the tab returns admins to their real role.
const VIEW_AS_KEY = "ttv.viewAsRole";
const readViewAs = () => {
    try { return sessionStorage.getItem(VIEW_AS_KEY) || ""; }
    catch { return ""; }
};
const writeViewAs = (v) => {
    try {
        if (v) sessionStorage.setItem(VIEW_AS_KEY, v);
        else sessionStorage.removeItem(VIEW_AS_KEY);
    } catch { /* ignore */ }
};

export function AuthProvider({ children }) {
    // undefined = loading, null = not auth
    const [realUser, setRealUser] = useState(undefined);
    const [error, setError] = useState(null);
    const [viewAsRole, setViewAsRole] = useState(readViewAs());

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setRealUser(data);
            return data;
        } catch {
            setRealUser(null);
            return null;
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Only admins may impersonate. If the underlying account isn't admin
    // any stored view-as is ignored and cleared.
    const effectiveViewAs = (realUser?.role === "admin" && viewAsRole) ? viewAsRole : "";

    useEffect(() => {
        if (viewAsRole && realUser && realUser.role !== "admin") {
            setViewAsRole(""); writeViewAs("");
        }
    }, [viewAsRole, realUser]);

    // Consumers see the effective role automatically. Backend cookie stays
    // as the real admin so API calls keep working.
    const user = useMemo(() => {
        if (!realUser) return realUser;
        if (!effectiveViewAs) return realUser;
        return { ...realUser, role: effectiveViewAs, _impersonating: true,
                 _real_role: realUser.role };
    }, [realUser, effectiveViewAs]);

    const login = async (payload, password) => {
        setError(null);
        try {
            const body = (typeof payload === "string")
                ? { username: payload, company_number: payload, password }
                : {
                    username: (payload.username ?? payload.company_number ?? "").trim(),
                    company_number: (payload.company_number ?? payload.username ?? "").trim(),
                    password: payload.password,
                    role: payload.role || undefined,
                    terminal: payload.terminal || "richards_bay",
                    email: (payload.email || "").trim() || undefined,
                };
            const { data } = await api.post("/auth/login", body);
            // Clear any lingering view-as when a new user logs in
            setViewAsRole(""); writeViewAs("");
            setRealUser(data);
            return data;
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (payload) => {
        setError(null);
        try {
            const body = {
                ...payload,
                username: (payload.username ?? payload.company_number ?? "").trim(),
                company_number: (payload.company_number ?? payload.username ?? "").trim(),
                terminal: payload.terminal || "richards_bay",
                email: (payload.email || "").trim() || undefined,
            };
            const { data } = await api.post("/auth/register", body);
            return data;
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setViewAsRole(""); writeViewAs("");
        setRealUser(null);
    };

    const startViewAs = (role) => {
        if (realUser?.role !== "admin") return;
        setViewAsRole(role || ""); writeViewAs(role || "");
    };
    const stopViewAs = () => { setViewAsRole(""); writeViewAs(""); };

    return (
        <AuthContext.Provider value={{
            user, realUser, error, login, register, logout, refresh,
            startViewAs, stopViewAs,
            isImpersonating: !!effectiveViewAs,
            viewAsRole: effectiveViewAs,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
